import { FlowPage } from '@monthio/design-system'
import { trackingService } from '@monthio/react-components'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { BudgetType } from '../../../../types/authenticateSmartcheckSessionDto'
import type {
	BudgetKeyWithPossibleRentIncluded,
	MissingInfoBudgetKey,
} from './missingBudgetInfoUtils'

import { budgetApiClient } from '../../../../api/budgetApiClient'
import { budgetEstimatesCalculatorService } from '../BudgetOverview/budgetEstimatesCalculatorService'
import { DocumentsUploader } from '../documentsUploader'
import { MissingInfoInputWithButton, MissingRentOptions } from './components'
import {
	getListOfCategoriesWithPossibleRentIncluded,
	isBudgetValueValid,
	MissingInfoBudgetKeys,
} from './missingBudgetInfoUtils'

interface MissingBudgetInfoProps {
	budgetEstimates: any
	miniBudgetType: BudgetType
	submitMissingBudgetInfo: () => void
}

const MissingSalaryContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

export const MissingBudgetInfo = ({
	budgetEstimates,
	miniBudgetType,
	submitMissingBudgetInfo,
}: MissingBudgetInfoProps) => {
	const { t } = useTranslation()
	const [files, setFiles] = useState<File[]>([])

	const budgetKeysWithPossibleRentIncluded: BudgetKeyWithPossibleRentIncluded[] =
		getListOfCategoriesWithPossibleRentIncluded(budgetEstimates)

	const [updateBudgetEstimates, setUpdateBudgetEstimates] = useState(
		budgetEstimatesCalculatorService.getEstimates(),
	)

	useEffect(() => {
		return budgetEstimatesCalculatorService.subscribe(() => {
			setUpdateBudgetEstimates({
				...budgetEstimatesCalculatorService.getEstimates(),
			})
		})
	}, [])

	const handleSubmitFiles = useCallback(() => {
		if (files.length > 0) {
			budgetApiClient.uploadFiles(files)
		}
	}, [files])

	const handleSubmit = useCallback(() => {
		handleSubmitFiles()
		submitMissingBudgetInfo()
	}, [handleSubmitFiles, submitMissingBudgetInfo])

	useEffect(() => {
		const prompts: { promptType: string; budgetEntry: string }[] = []
		for (const [name, key] of Object.entries(MissingInfoBudgetKeys)) {
			const validation = isBudgetValueValid(
				key,
				budgetEstimates,
				true,
				miniBudgetType,
				true,
			)
			if (!validation.isValid) {
				prompts.push({
					promptType: name,
					budgetEntry: key,
					...validation.details,
				})
			}
		}

		if (prompts.length > 0) {
			prompts.forEach((prompt) => {
				trackingService.trackEvent('promptTriggered', {
					...prompt,
				})
			})
		}
	}, [])

	const budgetKeys = [
		{
			budgetKey: MissingInfoBudgetKeys.Rent,
			confirmedState: useState(false),
			component:
				budgetKeysWithPossibleRentIncluded.length > 0 ? (
					<MissingRentOptions
						options={budgetKeysWithPossibleRentIncluded}
						budgetEstimates={budgetEstimates}
					/>
				) : (
					<MissingInfoInputWithButton
						budgetKey={MissingInfoBudgetKeys.Rent}
						handleNoInfoConfirmed={(confirmed) =>
							handleConfirmChange(MissingInfoBudgetKeys.Rent, confirmed)
						}
					/>
				),
		},
		{
			budgetKey: MissingInfoBudgetKeys.Insurance,
			confirmedState: useState(false),
			component: (
				<MissingInfoInputWithButton
					budgetKey={MissingInfoBudgetKeys.Insurance}
					handleNoInfoConfirmed={(confirmed) =>
						handleConfirmChange(MissingInfoBudgetKeys.Insurance, confirmed)
					}
				/>
			),
		},
		{
			budgetKey: MissingInfoBudgetKeys.CarLoanAndLeasing,
			confirmedState: useState(false),
			component: (
				<MissingInfoInputWithButton
					budgetKey={MissingInfoBudgetKeys.CarLoanAndLeasing}
					handleNoInfoConfirmed={(confirmed) =>
						handleConfirmChange(
							MissingInfoBudgetKeys.CarLoanAndLeasing,
							confirmed,
						)
					}
				/>
			),
		},
		{
			budgetKey: MissingInfoBudgetKeys.UnionFees,
			confirmedState: useState(false),
			component: (
				<MissingInfoInputWithButton
					budgetKey={MissingInfoBudgetKeys.UnionFees}
					handleNoInfoConfirmed={(confirmed) =>
						handleConfirmChange(MissingInfoBudgetKeys.UnionFees, confirmed)
					}
				/>
			),
		},
		{
			budgetKey: MissingInfoBudgetKeys.PhoneAndBroadband,
			confirmedState: useState(false),
			component: (
				<MissingInfoInputWithButton
					budgetKey={MissingInfoBudgetKeys.PhoneAndBroadband}
					handleNoInfoConfirmed={(confirmed) =>
						handleConfirmChange(
							MissingInfoBudgetKeys.PhoneAndBroadband,
							confirmed,
						)
					}
				/>
			),
		},
		{
			budgetKey: MissingInfoBudgetKeys.Salary,
			confirmedState: useState(false),
			component: (
				<MissingSalaryContainer>
					<MissingInfoInputWithButton
						budgetKey={MissingInfoBudgetKeys.Salary}
						handleNoInfoConfirmed={(confirmed) =>
							handleConfirmChange(MissingInfoBudgetKeys.Salary, confirmed)
						}
					/>
					<div>
						{t('miniBudgetForm-missingInfo-uploadDocumentation-description')}
					</div>
					<DocumentsUploader uploadedFiles={files} handleChange={setFiles} />
				</MissingSalaryContainer>
			),
		},
	]

	const handleConfirmChange = (
		key: MissingInfoBudgetKey,
		confirmed: boolean,
	) => {
		const budgetKey = budgetKeys.find((item) => item.budgetKey === key)
		if (budgetKey) {
			const [_, setConfirmed] = budgetKey.confirmedState
			setConfirmed(confirmed)
		}
	}

	const canSubmit = budgetKeys.every(
		({ budgetKey, confirmedState: [confirmed] }) =>
			isBudgetValueValid(
				budgetKey,
				updateBudgetEstimates,
				false,
				miniBudgetType,
			) || confirmed,
	)

	return (
		<FlowPage>
			<FlowPage.Content $spacing="xs">
				<FlowPage.Header
					title={t('miniBudgetForm-missingInfo-header-title')}
					description={t('miniBudgetForm-missingInfo-header-body')}
				/>
				{budgetKeys.map(({ budgetKey, component }) =>
					isBudgetValueValid(
						budgetKey,
						budgetEstimates,
						true,
						miniBudgetType,
					) ? null : (
						<div key={budgetKey}>{component}</div>
					),
				)}
			</FlowPage.Content>
			<FlowPage.Footer
				actions={
					<FlowPage.Cta onClick={handleSubmit} disabled={!canSubmit}>
						{t('consumerInputsPage-continue-text')}
					</FlowPage.Cta>
				}
			/>
		</FlowPage>
	)
}
